import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Redirect } from "react-router-dom";
import axios from "axios";
import { FaFingerprint, FaSave, FaSyncAlt } from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

function buildFullUrl(url, params) {
  if (!params) return url;
  const queryString = new URLSearchParams(params).toString();
  return queryString ? `${url}?${queryString}` : url;
}

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

function apiErrorMessage(err, fallback) {
  if (err?.response) {
    const status = err.response.status;
    if (status === 404) {
      return "Biometric API not found on server (404). Deploy updated PiZeroApi_Beta/ver1 files.";
    }
    const data = err.response.data;
    if (typeof data === "string" && data.trim()) {
      return data.slice(0, 180);
    }
    return data?.api_remark || err.response.statusText || fallback;
  }
  if (err?.message === "Network Error") {
    return "Network error — check browser console for the API URL and response.";
  }
  if (err?.message) {
    return err.message;
  }
  return fallback;
}

async function saveBiometricId({ schoolCode, staffId, biometricId }) {
  const params = {
    school_code: schoolCode,
    staff_id: String(staffId),
    biometric_id: biometricId,
  };

  const attempts = [
    {
      method: "get",
      url: `${API_BASE()}updateStaffBiometricId.php`,
      config: { params },
    },
    {
      method: "post",
      url: `${API_BASE()}updateStaffPersonalDetails.php`,
      config: {
        data: new URLSearchParams({
          update_action: "biometric_id",
          ...params,
        }),
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      },
    },
  ];

  let lastError = null;
  for (const attempt of attempts) {
    if (attempt.method === "get") {
      const fullUrl = buildFullUrl(attempt.url, params);
      console.log("[StaffBiometricId] Full URL:", fullUrl);
    } else {
      const body = attempt.config.data.toString();
      console.log("[StaffBiometricId] Full URL:", attempt.url);
      console.log("[StaffBiometricId] POST body:", body);
      console.log("[StaffBiometricId] Full request:", `${attempt.url}  body=${body}`);
    }
    try {
      const res =
        attempt.method === "get"
          ? await axios.get(attempt.url, attempt.config)
          : await axios.post(attempt.url, attempt.config.data, {
              headers: attempt.config.headers,
            });
      if (res.data?.api_status === "OK") {
        return { ok: true, data: res.data };
      }
      const remark = res.data?.api_remark || "Could not update biometric ID.";
      lastError = new Error(remark);
    } catch (e) {
      lastError = e;
      if (e?.response?.status === 404) {
        continue;
      }
      throw e;
    }
  }

  if (lastError) throw lastError;
  throw new Error("Could not update biometric ID.");
}

function staffRowKey(staff) {
  return staff?.staff_id ?? staff?.id ?? null;
}

function StaffBiometricId() {
  const loggedIn = localStorage.getItem("token") != null;
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState("");
  const [staffList, setStaffList] = useState([]);
  const [draftIds, setDraftIds] = useState({});
  const [search, setSearch] = useState("");

  const loadStaff = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      setStaffList([]);
      myToast.notify_warning("School code is missing.");
      return;
    }
    setLoading(true);
    try {
      const staffListParams = { school_code: schoolCode };
      const staffListUrl = `${API_BASE()}getStaff.php`;
      console.log("[StaffBiometricId] Full URL:", buildFullUrl(staffListUrl, staffListParams));
      const res = await axios.get(staffListUrl, { params: staffListParams });
      if (res.data?.api_status === "OK") {
        const rows = Array.isArray(res.data.staff) ? res.data.staff : [];
        setStaffList(rows);
        const initial = {};
        rows.forEach((s) => {
          const key = staffRowKey(s);
          if (key != null) {
            initial[key] = s.biometric_id ?? "";
          }
        });
        setDraftIds(initial);
      } else {
        setStaffList([]);
        myToast.notify_warning(res.data?.api_remark || "Could not load staff.");
      }
    } catch (e) {
      setStaffList([]);
      myToast.notify_warning(apiErrorMessage(e, "Network error loading staff."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  const filteredStaff = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return staffList;
    return staffList.filter((s) => {
      const key = staffRowKey(s);
      const hay = [
        s.staff_name,
        key,
        s.department_name,
        s.biometric_id,
        key != null ? draftIds[key] : "",
      ]
        .map((v) => (v ?? "").toString().toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [staffList, search, draftIds]);

  const missingCount = useMemo(
    () =>
      staffList.filter((s) => {
        const key = staffRowKey(s);
        return !(key != null ? draftIds[key] ?? "" : "").trim();
      }).length,
    [staffList, draftIds]
  );

  const handleDraftChange = useCallback((key, value) => {
    if (key == null) return;
    setDraftIds((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const handleSave = useCallback(
    async (staff) => {
      const schoolCode = SessionManager.getSchoolCode();
      const staffId = staffRowKey(staff);
      if (!schoolCode || staffId == null) return;

      const biometricId = String(draftIds[staffId] ?? "").trim();
      const savedId = String(staff.biometric_id ?? "").trim();
      if (biometricId === savedId) {
        myToast.notify_warning("No changes to save.");
        return;
      }

      setSavingId(String(staffId));
      try {
        await saveBiometricId({
          schoolCode,
          staffId,
          biometricId,
        });
        myToast.notify_success(`Biometric ID saved for ${staff.staff_name || "staff"}.`);
        setStaffList((prev) =>
          prev.map((row) =>
            staffRowKey(row) === staffId ? { ...row, biometric_id: biometricId } : row
          )
        );
        setDraftIds((prev) => ({
          ...prev,
          [staffId]: biometricId,
        }));
      } catch (e) {
        console.error("[StaffBiometricId] save biometric error", e);
        myToast.notify_warning(apiErrorMessage(e, "Network error saving biometric ID."));
      } finally {
        setSavingId("");
      }
    },
    [draftIds]
  );

  if (!loggedIn) return <Redirect to="/Login" />;
  if (loading) return <MyLoader />;

  return (
    <div
      className="content payroll-page sm-page"
      style={{
        background: COLORS.white,
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div className="content-header" style={{ background: COLORS.primary_color_shade5 }}>
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i className="fa fa-home" aria-hidden="true" style={{ color: COLORS.white }} />
              <Link to="/AdminMain/StaffManagement" style={{ color: COLORS.white }}>
                Staff Management
              </Link>
            </li>
            <li>
              <span style={{ color: COLORS.white }}> Biometric ID</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaFingerprint />
            </div>
            <div>
              <h1 className="sm-hero__title">Manage Biometric ID</h1>
              <p className="sm-hero__sub">
                {staffList.length} staff · {missingCount} without biometric ID
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <div className="sm-hero__search-group">
              <input
                type="text"
                className="sm-hero__search"
                placeholder="Search name, ID, department…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search staff"
              />
              <button
                type="button"
                className={`pp-icon-btn ${loading ? "spinning" : ""}`}
                onClick={loadStaff}
                disabled={loading}
                aria-label="Refresh"
              >
                <FaSyncAlt aria-hidden />
              </button>
            </div>
          </div>
        </header>

        <section className="sm-section sm-section--last">
          <div className="sm-bio-list">
            {filteredStaff.length === 0 ? (
              <div className="sm-empty">
                <p className="sm-empty__title">No staff found</p>
                <p className="sm-empty__hint">Try a different search or refresh the list.</p>
              </div>
            ) : (
              filteredStaff.map((staff) => {
                const staffId = staffRowKey(staff);
                if (staffId == null) return null;

                const savedValue = String(staff.biometric_id ?? "").trim();
                const draftValue = String(draftIds[staffId] ?? "").trim();
                const isDirty = draftValue !== savedValue;
                const isSaving = savingId === String(staffId);

                return (
                  <div key={String(staffId)} className="sm-bio-row">
                    <div className="sm-bio-row__who">
                      <div className="sm-bio-row__name">{staff.staff_name || "Unnamed"}</div>
                      <div className="sm-bio-row__meta">
                        <span>{staff.department_name || "—"}</span>
                        <span>Staff ID: {staffId || "—"}</span>
                      </div>
                    </div>
                    <div className="sm-bio-row__field">
                      <label className="sm-bio-row__label" htmlFor={`bio-${staffId}`}>
                        Biometric ID
                      </label>
                      <input
                        id={`bio-${staffId}`}
                        type="text"
                        className="form-control form-control-sm"
                        value={draftIds[staffId] ?? ""}
                        onChange={(e) => handleDraftChange(staffId, e.target.value)}
                        placeholder="Enter biometric ID"
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary sm-bio-row__save"
                      onClick={() => handleSave(staff)}
                      disabled={isSaving || !isDirty}
                    >
                      <FaSave aria-hidden /> {isSaving ? "Saving…" : "Save"}
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default StaffBiometricId;
